/**
 * Fast client-side fuzzy filter for Mantine Select / MultiSelect / Autocomplete.
 *
 * Strategy (priority order, all O(n) over catalog size ~60–500 — fine on every keystroke):
 *  1. Exact / prefix / substring on label + code (underscores → spaces)
 *  2. All query tokens match word prefixes ("ms exc" → "MS Excel")
 *  3. Contiguous subsequence (missed letters: "zndesk" → "zendesk")
 *  4. Bounded Levenshtein vs words / short labels (typos: "excell" → "excel")
 *
 * Groups are flattened, scored globally, then rebuilt so the best hits rise to the top.
 */

import type { ComboboxItem, ComboboxParsedItemGroup, OptionsFilter } from "@mantine/core";

const DEFAULT_LIMIT = 60;

type OptionGroup = ComboboxParsedItemGroup;

function normalize(raw: string): string {
  return (raw || "")
    .toLowerCase()
    .replace(/[_/|.-]+/g, " ")
    .replace(/[^a-z0-9+#.\s]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function isGroup(option: ComboboxItem | OptionGroup): option is OptionGroup {
  return typeof option === "object" && option !== null && "group" in option && Array.isArray((option as OptionGroup).items);
}

/** True if all chars of `needle` appear in order inside `hay` (allows skipped chars). */
function isSubsequence(needle: string, hay: string): boolean {
  if (!needle) return true;
  let i = 0;
  for (let j = 0; j < hay.length && i < needle.length; j++) {
    if (hay[j] === needle[i]) i++;
  }
  return i === needle.length;
}

function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  // Cap cost for long strings — only used for short words
  if (a.length > 24 || b.length > 24) return 99;
  const prev = new Array(b.length + 1);
  const cur = new Array(b.length + 1);
  for (let j = 0; j <= b.length; j++) prev[j] = j;
  for (let i = 1; i <= a.length; i++) {
    cur[0] = i;
    const ca = a.charCodeAt(i - 1);
    for (let j = 1; j <= b.length; j++) {
      const cost = ca === b.charCodeAt(j - 1) ? 0 : 1;
      cur[j] = Math.min(cur[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
    }
    for (let j = 0; j <= b.length; j++) prev[j] = cur[j];
  }
  return prev[b.length];
}

function scoreItem(item: ComboboxItem | null | undefined, q: string, extra = "", groupHint = ""): number {
  if (!item) return 0;
  const label = normalize(item.label ?? "");
  const value = normalize(String(item.value ?? "").replace(/_/g, " "));
  const extraN = normalize(extra);
  const group = normalize(typeof groupHint === "string" ? groupHint : String(groupHint ?? ""));
  const hay = [label, value, extraN, group].filter(Boolean).join(" ");
  if (!hay) return 0;

  if (label === q || value === q) return 1000;
  if (label.startsWith(q) || value.startsWith(q)) return 920;
  if (label.includes(q) || value.includes(q) || extraN.includes(q)) return 820;
  // State / group match (e.g. typing "punjab" lists Punjab cities)
  if (group && (group === q || group.startsWith(q) || group.includes(q))) return 610;

  const tokens = q.split(" ").filter(Boolean);
  const words = hay.split(" ").filter(Boolean);
  if (tokens.length > 0 && tokens.every((t) => words.some((w) => w.startsWith(t)))) {
    return 720;
  }

  const compactQ = q.replace(/\s/g, "");
  const compactHay = hay.replace(/\s/g, "");
  if (compactQ.length >= 3 && isSubsequence(compactQ, compactHay)) {
    return 520 + Math.max(0, 80 - (compactHay.length - compactQ.length));
  }

  // Typo tolerance: edit distance ≤ 2 against individual words or short full label
  if (q.length >= 3) {
    let best = 99;
    for (const w of words) {
      if (Math.abs(w.length - q.length) > 2) continue;
      best = Math.min(best, levenshtein(q, w));
    }
    if (label.length <= q.length + 4) {
      best = Math.min(best, levenshtein(q, label));
    }
    const maxDist = q.length <= 4 ? 1 : 2;
    if (best <= maxDist) {
      return 400 - best * 60;
    }
  }

  return 0;
}

type Scored = { item: ComboboxItem; group?: string; score: number };

function collectScored(
  options: (ComboboxItem | OptionGroup)[],
  q: string,
  extraSearchText?: (item: ComboboxItem) => string,
): Scored[] {
  const out: Scored[] = [];
  for (const opt of options) {
    if (opt == null) continue;
    if (isGroup(opt)) {
      const groupName = typeof opt.group === "string" ? opt.group : String(opt.group ?? "");
      for (const item of opt.items ?? []) {
        if (item == null || item.value == null) continue;
        const comboboxItem = item as ComboboxItem;
        const score = scoreItem(
          comboboxItem,
          q,
          extraSearchText?.(comboboxItem) ?? "",
          groupName,
        );
        if (score > 0) out.push({ item: comboboxItem, group: groupName, score });
      }
    } else {
      const comboboxItem = opt as ComboboxItem;
      if (comboboxItem.value == null && comboboxItem.label == null) continue;
      const score = scoreItem(comboboxItem, q, extraSearchText?.(comboboxItem) ?? "");
      if (score > 0) out.push({ item: comboboxItem, score });
    }
  }
  out.sort((a, b) => b.score - a.score || a.item.label.localeCompare(b.item.label));
  return out;
}

function groupKey(group: React.ReactNode): string {
  return typeof group === "string" ? group : String(group ?? "");
}

function rebuildGrouped(scored: Scored[], originalGroups: OptionGroup[]): OptionGroup[] {
  const byGroup = new Map<string, ComboboxItem[]>();
  for (const row of scored) {
    const g = row.group ?? "";
    const list = byGroup.get(g) ?? [];
    list.push(row.item);
    byGroup.set(g, list);
  }
  const result: OptionGroup[] = [];
  for (const g of originalGroups) {
    const items = byGroup.get(groupKey(g.group));
    if (items?.length) result.push({ group: g.group, items });
  }
  // Any leftover groups not in original order
  for (const [group, items] of byGroup) {
    if (!result.some((g) => groupKey(g.group) === group)) {
      result.push({ group, items });
    }
  }
  return result;
}

function buildCatalogFuzzyFilter(
  extraSearchText?: (item: ComboboxItem) => string,
): OptionsFilter {
  return ({ options, search, limit }) => {
    const q = normalize(search);
    // Empty query: show everything (or honor an explicit finite `limit`).
    // Active search: rank + cap so large catalogs stay snappy.
    const cap =
      Number.isFinite(limit) && limit > 0
        ? limit
        : q
          ? DEFAULT_LIMIT
          : Number.POSITIVE_INFINITY;

    if (!q) {
      if (options.length && isGroup(options[0] as ComboboxItem | OptionGroup)) {
        if (!Number.isFinite(cap)) return options;
        const groups = options as OptionGroup[];
        let remaining = cap;
        const trimmed: OptionGroup[] = [];
        for (const g of groups) {
          if (remaining <= 0) break;
          const items = g.items.slice(0, remaining);
          remaining -= items.length;
          if (items.length) trimmed.push({ group: g.group, items });
        }
        return trimmed;
      }
      return Number.isFinite(cap) ? (options as ComboboxItem[]).slice(0, cap) : options;
    }

    const scored = collectScored(
      options as (ComboboxItem | OptionGroup)[],
      q,
      extraSearchText,
    ).slice(0, cap);

    if (options.length && isGroup(options[0] as ComboboxItem | OptionGroup)) {
      return rebuildGrouped(scored, options as OptionGroup[]);
    }
    return scored.map((s) => s.item);
  };
}

/**
 * Drop-in Mantine `filter` for skill, segment & city catalogs.
 * Pass `limit` on the Select/MultiSelect — honored here.
 */
export const catalogFuzzyFilter: OptionsFilter = buildCatalogFuzzyFilter();

/** Factory when options need extra haystack text (e.g. city aliases / state). */
export function makeCatalogFuzzyFilter(
  extraSearchText: (item: ComboboxItem) => string,
): OptionsFilter {
  return buildCatalogFuzzyFilter(extraSearchText);
}

/** Score free text (+ optional code) for ranking outside Mantine filters. */
export function fuzzyScoreText(label: string, search: string, value = ""): number {
  const q = normalize(search);
  if (!q) return 1;
  return scoreItem({ label, value }, q);
}

/** Fuzzy filter for Autocomplete `data: string[]` (e.g. RequiredSkillsEditor). */
export function fuzzyFilterStringList(data: string[], search: string, limit = DEFAULT_LIMIT): string[] {
  const q = normalize(search);
  if (!q) return data.slice(0, limit);
  const scored = data
    .map((label) => ({ label, score: scoreItem({ value: label, label }, q) }))
    .filter((r) => r.score > 0)
    .sort((a, b) => b.score - a.score || a.label.localeCompare(b.label))
    .slice(0, limit);
  return scored.map((r) => r.label);
}
